import Gleap from 'gleap';
import type { User } from '@supabase/supabase-js';

const ANON_ID_KEY = 'gleap_anon_id';
const RECENT_ACTIONS_LIMIT = 5;
const MAX_STRING_LENGTH = 300;

let gleapInitialized = false;
let recentActions: Array<Record<string, unknown>> = [];

const ALLOWED_GLEAP_PROPERTIES = new Set([
  'tool_id',
  'tool_name',
  'project_name',
  'project_id',
  'calculation_id',
  'export_format',
  'path',
  'page',
  'search',
  'error_message',
  'error_name',
  'error_stack',
  'component_stack',
  'old_name',
  'new_name',
  'total_messages',
  'participant_count',
  'timestamp',
  'app_version',
  'app_environment',
  'appEnvironment',
  'appVersion',
  'page_name',
  'file_name',
  'file_size',
  'analysis_duration_ms',
]);

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function isGleapEnabled(): boolean {
  const enabledFlag = import.meta.env.VITE_GLEAP_ENABLED;
  return enabledFlag === undefined || enabledFlag === 'true' || enabledFlag === true;
}

function shouldUseGleapCookies(): boolean {
  return import.meta.env.VITE_GLEAP_USE_COOKIES === 'true';
}

function getAppEnvironment(): 'dev' | 'staging' | 'prod' {
  const mode = import.meta.env.MODE;
  if (mode === 'development') return 'dev';
  if (mode === 'staging') return 'staging';
  return 'prod';
}

function getAppVersion(): string {
  return import.meta.env.VITE_APP_VERSION || 'unknown';
}

function createAnonId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `anon_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function getOrCreateAnonId(): string {
  if (!isBrowser()) return 'anon_server';

  try {
    const existing = window.localStorage.getItem(ANON_ID_KEY);
    if (existing) return existing;
    const created = createAnonId();
    window.localStorage.setItem(ANON_ID_KEY, created);
    return created;
  } catch {
    return createAnonId();
  }
}

function sanitizeValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH ? value.slice(0, MAX_STRING_LENGTH) : value;
  }
  if (Array.isArray(value)) {
    return value.slice(0, 10);
  }
  return value;
}

export function sanitizeGleapEventProperties(
  payload?: Record<string, unknown>
): Record<string, unknown> {
  if (!payload) return {};

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (!ALLOWED_GLEAP_PROPERTIES.has(key)) continue;
    sanitized[key] = sanitizeValue(value);
  }

  return sanitized;
}

function recordRecentAction(action: Record<string, unknown>): void {
  recentActions = [action, ...recentActions].slice(0, RECENT_ACTIONS_LIMIT);
}

export function getRecentGleapActions(): Array<Record<string, unknown>> {
  return [...recentActions];
}

function ensureInitialized(): boolean {
  if (!isBrowser()) return false;
  if (!isGleapEnabled()) {
    console.info('[Gleap] Disabled via VITE_GLEAP_ENABLED');
    return false;
  }
  if (gleapInitialized) return true;

  const apiKey = import.meta.env.VITE_GLEAP_API_KEY;
  if (!apiKey) {
    console.warn(
      '[Gleap] API key missing. Set VITE_GLEAP_API_KEY environment variable.',
      '\nCurrent environment:', getAppEnvironment(),
      '\nVITE_GLEAP_ENABLED:', import.meta.env.VITE_GLEAP_ENABLED ?? 'undefined (defaults to true)'
    );
    return false;
  }

  try {
    Gleap.initialize(apiKey);
    console.info('[Gleap] Initialized successfully in', getAppEnvironment(), 'environment');

    try {
      Gleap.setUseCookies(shouldUseGleapCookies());
    } catch (error) {
      console.warn('[Gleap] Failed to set cookie preference:', error);
    }

    Gleap.setEnvironment(getAppEnvironment());

    try {
      Gleap.showFeedbackButton(true);
      console.info('[Gleap] Feedback button enabled');
    } catch (error) {
      console.warn('[Gleap] Failed to show feedback button:', error);
    }

    gleapInitialized = true;
  } catch (error) {
    console.error('[Gleap] Initialization failed:', error);
    return false;
  }

  return true;
}

export function initGleap(): void {
  ensureInitialized();
}

export function identifyGleapUser(user: User): void {
  if (!ensureInitialized()) return;

  const name =
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    user.user_metadata?.display_name;
  const avatarUrl = user.user_metadata?.avatar_url || user.user_metadata?.picture;
  const jobTitle = user.user_metadata?.job_title;

  const traits: Record<string, unknown> = {
    name,
    email: user.email || undefined,
    avatarUrl,
    jobTitle,
    emailVerified: !!user.email_confirmed_at,
    appEnvironment: getAppEnvironment(),
    appVersion: getAppVersion(),
    org_id: user.user_metadata?.org_id || user.app_metadata?.org_id,
    org_name: user.user_metadata?.org_name || user.user_metadata?.company,
    team_id: user.user_metadata?.team_id,
    team_name: user.user_metadata?.team_name,
    role: user.user_metadata?.role || user.app_metadata?.role,
    plan: user.user_metadata?.plan || user.app_metadata?.plan,
    seat_count: user.user_metadata?.seat_count,
  };

  try {
    Gleap.identify(user.id, traits);
  } catch (error) {
    console.warn('Failed to identify user in Gleap:', error);
  }
}

export function identifyGleapAnonymous(): void {
  if (!ensureInitialized()) return;

  const anonId = getOrCreateAnonId();
  try {
    Gleap.identify(anonId, {
      name: 'Anonymous',
      customData: {
        isAnonymous: true,
        appEnvironment: getAppEnvironment(),
        appVersion: getAppVersion(),
      },
    });
  } catch (error) {
    console.warn('Failed to identify anonymous user in Gleap:', error);
  }
}

export function clearGleapIdentity(): void {
  if (!ensureInitialized()) return;

  try {
    Gleap.clearIdentity();
  } catch (error) {
    console.warn('Failed to clear Gleap identity:', error);
  }
}

export function setGleapCustomData(data: Record<string, unknown>): void {
  if (!ensureInitialized()) return;

  try {
    Gleap.attachCustomData(data);
  } catch (error) {
    console.warn('Failed to set Gleap custom data:', error);
  }
}

export function setGleapTags(tags: string[]): void {
  if (!ensureInitialized()) return;

  try {
    Gleap.setTags(tags);
  } catch (error) {
    console.warn('Failed to set Gleap tags:', error);
  }
}

export function trackGleapEvent(
  eventName: string,
  payload?: Record<string, unknown>
): void {
  if (!ensureInitialized()) return;

  const sanitizedPayload = sanitizeGleapEventProperties(payload);

  try {
    Gleap.trackEvent(eventName, {
      ...sanitizedPayload,
      timestamp: new Date().toISOString(),
      appEnvironment: getAppEnvironment(),
      appVersion: getAppVersion(),
    });
    recordRecentAction({
      name: eventName,
      ...sanitizedPayload,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.warn('Failed to track Gleap event:', error);
  }
}

export function reportGleapError(
  error: Error,
  context?: Record<string, unknown>
): void {
  trackGleapEvent('error', {
    error_message: error.message,
    error_name: error.name,
    error_stack: error.stack,
    ...context,
  });
}

export function updateGleapContact(properties: Record<string, unknown>): void {
  if (!ensureInitialized()) return;
  try {
    Gleap.updateContact(properties);
  } catch (error) {
    console.warn('Failed to update Gleap contact:', error);
  }
}

export function openGleap(): void {
  if (!ensureInitialized()) return;
  try {
    Gleap.open();
  } catch (error) {
    console.warn('Failed to open Gleap:', error);
  }
}

export function softReinitializeGleap(): void {
  if (!ensureInitialized()) return;
  try {
    const instance = Gleap.getInstance?.();
    instance?.softReInitialize?.();
  } catch (error) {
    console.warn('Failed to soft reinitialize Gleap:', error);
  }
}
